// theme/index.ts
// Main theme export combining all theme modules

import { createTheme, alpha, Theme } from '@mui/material/styles';
import { palette, customColors, gradients } from './palette';
import type { CustomColors, Gradients } from './palette';
import { typography, fontImports } from './typography';
import type { ExtendedTypographyOptions } from './typography';

// Direct color references for component overrides
// (Using direct values avoids TypeScript issues with PaletteColorOptions union types)
const colors = {
  primary: { main: '#00FFD4', light: '#33FFE0', contrastText: '#0A0E14' },
  secondary: { main: '#7B68EE', light: '#9D8FFF', contrastText: '#FFFFFF' },
  error: { main: '#FF3366', light: '#FF6B8A', contrastText: '#FFFFFF' },
  background: { default: '#0A0E14', paper: '#1E2530', card: '#252D3A', elevated: '#2E3744' },
  text: { primary: '#FFFFFF', secondary: '#8892A0', disabled: '#4A5568' },
};

// Create the MUI theme
export const theme: Theme = createTheme({
  palette,
  typography,

  // Shape configuration
  shape: {
    borderRadius: 8,
  },

  // Spacing (default is 8px)
  spacing: 8,

  // Breakpoints
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },

  // Component overrides
  components: {
    // Button overrides
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: '"Rajdhani", sans-serif',
          fontWeight: 600,
          borderRadius: '8px',
          textTransform: 'none',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
          },
        },
        containedPrimary: {
          backgroundColor: colors.primary.main,
          color: colors.primary.contrastText,
          '&:hover': {
            backgroundColor: colors.primary.light,
            boxShadow: customColors.glows.primary,
          },
        },
        containedSecondary: {
          backgroundColor: colors.secondary.main,
          color: colors.secondary.contrastText,
          '&:hover': {
            backgroundColor: colors.secondary.light,
            boxShadow: customColors.glows.info,
          },
        },
        containedError: {
          backgroundColor: colors.error.main,
          color: colors.error.contrastText,
          '&:hover': {
            backgroundColor: colors.error.light,
            boxShadow: customColors.glows.danger,
          },
        },
        outlined: {
          borderColor: 'rgba(255, 255, 255, 0.3)',
          '&:hover': {
            borderColor: colors.primary.main,
            backgroundColor: alpha(colors.primary.main, 0.1),
          },
        },
      },
    },

    // Paper overrides
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: colors.background.paper,
        },
      },
    },

    // Card overrides
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.card,
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '12px',
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: 'rgba(255, 255, 255, 0.2)',
          },
        },
      },
    },

    // TextField overrides
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: colors.background.card,
            borderRadius: '8px',
            transition: 'all 0.3s ease',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.1)',
            },
            '&:hover fieldset': {
              borderColor: colors.primary.main,
            },
            '&.Mui-focused fieldset': {
              borderColor: colors.primary.main,
              boxShadow: `0 0 15px ${alpha(colors.primary.main, 0.2)}`,
            },
          },
          '& .MuiInputLabel-root': {
            color: colors.text.secondary,
            '&.Mui-focused': {
              color: colors.primary.main,
            },
          },
          '& .MuiOutlinedInput-input': {
            color: colors.text.primary,
          },
        },
      },
    },

    // Dialog overrides
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.background.paper,
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '12px',
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: '"Rajdhani", sans-serif',
          fontWeight: 600,
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        },
      },
    },

    // AppBar overrides
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.paper,
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
        },
      },
    },

    // Drawer overrides
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.background.paper,
          borderRight: '1px solid rgba(255, 255, 255, 0.1)',
        },
      },
    },

    // List item overrides
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          margin: '4px 8px',
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateX(4px)',
          },
        },
      },
    },

    // Chip overrides
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: '"Share Tech Mono", monospace',
          borderRadius: '16px',
        },
      },
    },

    // Tooltip overrides
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.background.elevated,
          border: '1px solid rgba(255, 255, 255, 0.1)',
          fontFamily: '"Exo 2", sans-serif',
          fontSize: '0.75rem',
        },
      },
    },

    // Table overrides
    MuiTable: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.paper,
        },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontFamily: '"Rajdhani", sans-serif',
            fontWeight: 600,
            color: colors.text.secondary,
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        },
      },
    },

    // Accordion overrides
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.card,
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '8px !important',
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: '8px 0',
          },
        },
      },
    },

    // Tab overrides
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: '"Rajdhani", sans-serif',
          fontWeight: 500,
          textTransform: 'none',
          '&.Mui-selected': {
            color: colors.primary.main,
          },
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: colors.primary.main,
          height: '3px',
          borderRadius: '3px 3px 0 0',
        },
      },
    },
  },
});

// Export additional theme utilities
export { palette, customColors, gradients };
export type { CustomColors, Gradients };
export { typography, fontImports };
export type { ExtendedTypographyOptions };

// CSS variable mapping type
export interface CssVariables {
  '--bg-primary': string;
  '--bg-secondary': string;
  '--bg-card': string;
  '--bg-elevated': string;
  '--accent-primary': string;
  '--accent-danger': string;
  '--accent-warning': string;
  '--accent-success': string;
  '--accent-info': string;
  '--text-primary': string;
  '--text-secondary': string;
  '--text-muted': string;
}

// CSS variable mapping for use in raw CSS
export const cssVariables: CssVariables = {
  '--bg-primary': colors.background.default,
  '--bg-secondary': colors.background.paper,
  '--bg-card': colors.background.card,
  '--bg-elevated': colors.background.elevated,
  '--accent-primary': colors.primary.main,
  '--accent-danger': colors.error.main,
  '--accent-warning': '#FFE600',
  '--accent-success': '#00FF88',
  '--accent-info': colors.secondary.main,
  '--text-primary': colors.text.primary,
  '--text-secondary': colors.text.secondary,
  '--text-muted': colors.text.disabled,
};

export default theme;
